import React, { useRef, useEffect } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5radar from "@amcharts/amcharts5/radar"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"

// Utils
import { formatDataForMultiGauge, getMaxValue, getTotalValue } from "./utils"
import { getNumberFormat, transformeData } from "../utils/global"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  configureNumberFormatting,
  configureColorSet,
} from "../utils/utils"

// Types
interface SolidGaugeChartProps {
  idChart?: string
  width?: string
  height?: string
  dataChart?: any[]
  defaultDataChart?: any[]
  startAngle?: number
  endAngle?: number | string
  innerRadius?: number
  enableExportMenu?: boolean
  widgetTitle?: string
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  visibilityLegend?: boolean
  legendPosition?: "top" | "bottom" | "left" | "right"
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  valueForceMinMax?: boolean
  categoryData?: string | string[]
  dependsOnField?: string
  minGridDistance?: number
  hideValues?: boolean
  hideLabelIfPercentTooSmall?: boolean
  valuePercentUnderWhichHideLabel?: number
  dataNames?: string[]
  valueData?: string[]
  columnStrokeWidth?: number
  columnCornerRadius?: number
  colorSetTheme?: string[]
  enableToolTip?: boolean
  typeOfMaxValue?: "max" | "total" | "percent"
  hideCentralValue?: boolean
  typeOfCentralValue?: "value" | "percent" | "value_and_percent" | "value_percent_custom" | "custom"
  fontSizeValueTitleCenter?: string
  fontWeightValueTitleCenter?: string
  colorValueTitleCenter?: string
  fontSizeSubTitleCenter?: string
  fontWeightSubTitleCenter?: string
  colorSubTitleCenter?: string
  fontSizeCustomLabelCenter?: string
  fontWeightCustomLabelCenter?: string
  colorCustomLabelCenter?: string
  customLabelInput?: string
  hideAxisValues?: boolean
  showAsPercentage?: boolean
  requestLoad?: any
  fieldsData?: any
}

const SolidGaugeChart: React.FC<SolidGaugeChartProps> = ({
  idChart,
  width = "100%",
  height = "100%",
  dataChart = [],
  defaultDataChart = [],
  startAngle = 0,
  endAngle = 270,
  innerRadius = 20,
  enableExportMenu = false,
  widgetTitle = "multiGauge",
  numberDecimal = 2,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  visibilityLegend = false,
  legendPosition = "right",
  displayTypeLegendLabels = "truncate",
  valueForceMinMax = true,
  categoryData = "country",
  dependsOnField = "",
  minGridDistance = 10,
  hideValues = false,
  hideLabelIfPercentTooSmall = false,
  valuePercentUnderWhichHideLabel = 5,
  dataNames = ["value1"],
  valueData = ["value1"],
  columnStrokeWidth = 0,
  columnCornerRadius = 10,
  colorSetTheme = [],
  enableToolTip = true,
  typeOfMaxValue = "total",
  hideCentralValue = false,
  typeOfCentralValue = "value",
  fontSizeValueTitleCenter = "1rem",
  fontWeightValueTitleCenter = "normal",
  colorValueTitleCenter = "#707070",
  fontSizeSubTitleCenter = "1rem",
  fontWeightSubTitleCenter = "normal",
  colorSubTitleCenter = "#707070",
  fontSizeCustomLabelCenter = "1rem",
  fontWeightCustomLabelCenter = "normal",
  colorCustomLabelCenter = "#707070",
  customLabelInput = "",
  hideAxisValues = false,
  showAsPercentage = false,
  requestLoad,
  fieldsData,
}) => {
  const ID = useRef(idChart || `MultiGauge_${Math.random()}`).current
  const chartRef = useRef<am5radar.RadarChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const dataTransformed = transformeData(requestLoad, fieldsData, dataChart)

  useEffect(() => {
    // Create root
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    // Set themes
    root.setThemes([am5themes_Animated.new(root)])

    // Create chart
    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: startAngle - 90, // To start like a clock
        endAngle: (typeof endAngle === "number" ? endAngle : parseFloat(endAngle)) - 90,
      })
    )

    chartRef.current = chart

    // Configure chart
    setupChart(chart, root)

    // Initial data render
    updateChart(chart, root)

    return () => {
      root.dispose()
    }
  }, [])

  // Effect for updates - runs on every render (like old amCharts 4 code)
  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Clear legends before update
      clearLegends(root.container)

      // Update chart properties for reactivity
      chart.set("startAngle", startAngle - 90)
      chart.set("endAngle", (typeof endAngle === "number" ? endAngle : parseFloat(endAngle)) - 90)
      chart.set("innerRadius", am5.percent(innerRadius))

      updateChart(chart, root)
    }
  })

  const setupChart = (chart: am5radar.RadarChart, root: am5.Root) => {
    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    // Inner radius
    if (
      dependsOnField &&
      Array.isArray(valueData) &&
      valueData.length === 1 &&
      categoryData === ""
    ) {
      chart.set("innerRadius", am5.percent(70))
    } else {
      chart.set("innerRadius", am5.percent(innerRadius))
    }

    // Color set
    configureColorSet(chart, root, colorSetTheme)

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Legend - use root.container to avoid layout corruption
    const legend = createLegend(root.container, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      showValueLabels: true,
      legendLabelText: "{category}",
    })
  }

  const updateChart = (chart: am5radar.RadarChart, root: am5.Root) => {
    // Clear existing axes and series
    chart.xAxes.clear()
    chart.yAxes.clear()
    chart.series.clear()

    // Re-run setup to apply any changed settings (legend, colors, etc.)
    setupChart(chart, root)

    // Format data
    const formattedData =
      dataChart && dataChart.length > 0
        ? formatDataForMultiGauge(
            dataChart,
            7,
            typeOfMaxValue,
            valueData,
            categoryData,
            dependsOnField,
            dataNames
          )
        : formatDataForMultiGauge(
            defaultDataChart,
            7,
            typeOfMaxValue,
            ["value1"],
            "country",
            dependsOnField,
            dataNames
          )

    // Determine category field
    let categoryField: string
    if (Array.isArray(categoryData) && categoryData[0]) {
      categoryField = categoryData[0]
    } else if (typeof categoryData === "string" && categoryData !== "") {
      categoryField = categoryData
    } else {
      categoryField = "category"
    }

    // Create axes
    // X-axis (Value)
    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        strictMinMax: valueForceMinMax,
        renderer: am5radar.AxisRendererCircular.new(root, {
          strokeOpacity: 0,
        }),
      })
    )

    // Calculate max value
    if (typeOfMaxValue === "total") {
      xAxis.set("max", getTotalValue(formattedData, "value"))
    } else if (typeOfMaxValue === "max") {
      xAxis.set("max", getMaxValue(formattedData, "value"))
    } else {
      xAxis.set("max", 100)
    }

    if (dependsOnField) {
      xAxis.set("max", 100)
    }

    xAxis.get("renderer").labels.template.set("visible", !hideAxisValues)
    xAxis.get("renderer").grid.template.set("visible", false)

    // Y-axis (Category)
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: categoryField,
        renderer: am5radar.AxisRendererRadial.new(root, {
          minGridDistance: minGridDistance,
        }),
      })
    )

    yAxis.data.setAll(formattedData)

    yAxis.get("renderer").grid.template.setAll({
      location: 0,
      strokeOpacity: 0,
    })

    // Hide labels based on hideValues or when it's a full 360 degree chart
    const shouldHideLabels = hideValues || Number(endAngle) === 360

    yAxis.get("renderer").labels.template.setAll({
      visible: !shouldHideLabels,
      centerX: am5.p100,
    })

    // Special handling for semi-circle (endAngle close to 0)
    if (Number(endAngle) === 0.1 || Number(endAngle) < 1) {
      yAxis.get("renderer").labels.template.setAll({
        maxWidth: 100,
        oversizedBehavior: "truncate",
        tooltipText: "{name}",
      })
    }

    // Label color adapter
    yAxis.get("renderer").labels.template.adapters.add("fill", (fill, target: any) => {
      const dataItem = target.dataItem
      if (dataItem && dataItem.index >= 0) {
        return chart.get("colors")?.getIndex(dataItem.index)
      }
      return fill
    })

    // Determine value field - use 'value' or 'percent'
    let valueField = "value"
    let seriesName = dataNames[0]

    // If we have dependsOnField, we use 'percent' instead of 'value'
    if (dependsOnField && dependsOnField.length) {
      valueField = "percent"
    }

    // For single gauge with dependsOn, use special name
    if (
      Array.isArray(valueData) &&
      valueData.length === 1 &&
      dependsOnField &&
      dependsOnField.length &&
      categoryData === ""
    ) {
      seriesName = "dependsOn"
    }

    // Background series (like series1 in docs)
    const series1 = chart.series.push(
      am5radar.RadarColumnSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        clustered: false,
        valueXField: "full",
        categoryYField: categoryField,
        fill: root.interfaceColors.get("alternativeBackground"),
      })
    )

    series1.columns.template.setAll({
      width: am5.p100,
      fillOpacity: 0.08,
      strokeOpacity: 0,
      cornerRadius: columnCornerRadius,
    })

    series1.data.setAll(formattedData)

    // Data series (like series2 in docs)
    const series2 = chart.series.push(
      am5radar.RadarColumnSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        clustered: false,
        valueXField: valueField,
        categoryYField: categoryField,
      })
    )

    series2.columns.template.setAll({
      width: am5.p100,
      strokeOpacity: 0,
      cornerRadius: columnCornerRadius,
    })

    // Color adapter
    series2.columns.template.adapters.add("fill", (fill, target: any) => {
      const dataItem = target.dataItem
      if (dataItem) {
        // Get index from series dataItems array
        const index = series2.dataItems.indexOf(dataItem)
        if (index >= 0) {
          return chart.get("colors")?.getIndex(index)
        }
      }
      return fill
    })

    // Tooltip
    if (enableToolTip) {
      let tooltipText = `{categoryY}: [bold]{valueX}[/]`

      if (showAsPercentage || dependsOnField) {
        tooltipText = `{categoryY}: [bold]{valueX}%[/]`
      }

      if (dependsOnField && Array.isArray(valueData) && valueData.length === 1) {
        tooltipText = `{categoryY}: [bold]{valueX}%[/]\n{${valueData[0]}} / {${dependsOnField}}`
      } else if (
        Array.isArray(valueData) &&
        valueData.length > 1 &&
        dependsOnField &&
        dependsOnField.length &&
        categoryData === ""
      ) {
        tooltipText = `{categoryY}: [bold]{valueX}%[/]\n{value} / {${dependsOnField}}`
      }

      series2.columns.template.set("tooltipText", tooltipText)
    }

    series2.data.setAll(formattedData)

    // Central value label for single gauge
    if (
      Array.isArray(valueData) &&
      valueData.length === 1 &&
      dependsOnField &&
      dependsOnField.length &&
      categoryData === "" &&
      !hideCentralValue
    ) {
      const centralLabel = chart.radarContainer.children.push(
        am5.Label.new(root, {
          centerX: am5.p50,
          centerY: am5.p50,
          textAlign: "center",
        })
      )

      series2.events.on("datavalidated", () => {
        if (formattedData && formattedData.length > 0) {
          const firstData = formattedData[0]
          const value = firstData[valueData[0]] || 0
          const percent = firstData.percent || 0

          let labelText = ""
          if (typeOfCentralValue === "value") {
            labelText = `[fontSize: ${fontSizeValueTitleCenter} fontWeight: ${fontWeightValueTitleCenter} ${colorValueTitleCenter}]${value}[/]`
          } else if (typeOfCentralValue === "value_and_percent") {
            labelText = `[fontSize: ${fontSizeValueTitleCenter} fontWeight: ${fontWeightValueTitleCenter} ${colorValueTitleCenter}]${value}[/]\n[fontSize: ${fontSizeSubTitleCenter} fontWeight: ${fontWeightSubTitleCenter} ${colorSubTitleCenter}]${percent.toFixed(
              1
            )}%[/]`
          } else if (typeOfCentralValue === "value_percent_custom") {
            labelText = `[fontSize: ${fontSizeValueTitleCenter} fontWeight: ${fontWeightValueTitleCenter} ${colorValueTitleCenter}]${value}[/]\n[fontSize: ${fontSizeSubTitleCenter} fontWeight: ${fontWeightSubTitleCenter} ${colorSubTitleCenter}]${percent.toFixed(
              1
            )}%[/]\n[fontSize: ${fontSizeCustomLabelCenter} fontWeight: ${fontWeightCustomLabelCenter} ${colorCustomLabelCenter}]${customLabelInput}[/]`
          } else if (typeOfCentralValue === "custom") {
            labelText = `[fontSize: ${fontSizeCustomLabelCenter} fontWeight: ${fontWeightCustomLabelCenter} ${colorCustomLabelCenter}]${customLabelInput}[/]`
          }

          centralLabel.set("text", labelText)
        }
      })
    }

    // Populate legend data
    const legend = root.container.children.values.find(child => child instanceof am5.Legend) as
      | am5.Legend
      | undefined
    if (legend) {
      legend.data.setAll(series2.dataItems)
    }
  }

  return <div id={ID} style={{ width, height }} />
}

export default SolidGaugeChart
